import React, { useState, useEffect, useRef } from 'react';
import { AiOutlineClose } from 'react-icons/ai'; // Import the "X" icon
import { cn } from '../../utils/helpers';
import './CMSModal.css';

type Props = {
    trigger?: React.ReactNode | string;
	content: string | React.ReactNode;
	title: string | React.ReactNode;
	image?: React.ReactNode;
	centerTitle?: boolean;
	className?: string;
	subtitle?: string | React.ReactNode;
	overlayClassName?: string;
	overlayZIndex?: number;
	disableBodyScrollLock?: boolean;
    portalRootId?: string;
    isOpen?: boolean;
    onClose?: () => void;
    onOpen?: () => void;
};
const CMSModal: React.FC<Props> = ({ trigger, content, title, image, centerTitle, className, subtitle, overlayClassName, overlayZIndex, disableBodyScrollLock, portalRootId = 'modal-root', isOpen: isOpenProp, onClose, onOpen }) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
	const modalRef = useRef<HTMLDivElement>(null); // Reference to the modal content

    const isControlled = typeof isOpenProp === 'boolean';
    const isOpen = isControlled ? (isOpenProp as boolean) : uncontrolledOpen;
    const openModal = () => {
        if (!isControlled) setUncontrolledOpen(true);
        onOpen?.();
    };
    const closeModal = () => {
        if (!isControlled) setUncontrolledOpen(false);
        onClose?.();
    };

	// Close the modal if clicked outside of it or if Esc key is pressed
	const handleKeyDown = (event: KeyboardEvent) => {
		if (event.key === 'Escape') {
			closeModal(); // Close modal if Esc is pressed
		}
	};

	const handleClickOutside = (event: MouseEvent) => {
		if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
			closeModal(); // Close modal if click is outside
		}
	};

	useEffect(() => {
		// Disable scrolling when the modal is open
        if (!disableBodyScrollLock) {
			if (isOpen) {
				document.body.style.overflow = 'hidden';
			} else {
				document.body.style.overflow = '';
			}
		}

        // Toggle body class to coordinate z-index with sticky elements
        if (isOpen) {
            document.body.classList.add('modal-open');
            const sticky = document.getElementById('sticky-submit-container');
            if (sticky) {
                sticky.style.zIndex = '0';
                sticky.style.pointerEvents = 'none';
            }
        } else {
            document.body.classList.remove('modal-open');
            const sticky = document.getElementById('sticky-submit-container');
            if (sticky) {
                sticky.style.zIndex = '';
                sticky.style.pointerEvents = '';
            }
        }

		// Add event listeners
		if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
			document.addEventListener('keydown', handleKeyDown);
		} else {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleKeyDown);
		}

		// Cleanup the event listeners on component unmount or when modal is closed
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleKeyDown);
            document.body.classList.remove('modal-open');
            const sticky = document.getElementById('sticky-submit-container');
            if (sticky) {
                sticky.style.zIndex = '';
                sticky.style.pointerEvents = '';
            }
		};
	}, [isOpen]);

    // No portal usage; we'll lower sticky z-index when open to ensure overlay covers it

	return (
		<>
            {!isControlled && trigger ? (
                <span className="inline" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openModal(); }}>{trigger}</span>
            ) : null}
            {isOpen && (
                <div 
                    className={`cms-modal-overlay fixed top-0 left-0 w-full h-full flex justify-center items-center ${overlayClassName ?? 'bg-[#0d2c54] bg-opacity-90 z-50'}`} 
                    style={{ zIndex: overlayZIndex ?? 99999, willChange: 'opacity' }}
                    onClick={(e) => { e.stopPropagation(); closeModal(); }}
                >
                        <div
                    ref={modalRef}
                    className={cn(
                        'bg-white relative rounded-[38px_7.5px] flex flex-col justify-between mx-8 transform transition-all duration-300 ease-in-out max-w-[400px] w-full max-h-[500px]',
                        className
                    )}
                        onClick={(e) => e.stopPropagation()}
                >
                    <div id="header" className="flex flex-row w-full justify-center items-center">
                        <button
                            onClick={() => closeModal()}
                            className="absolute top-4 right-4 w-6 h-6 p-0 bg-transparent border-none flex justify-center items-center"
                        >
                            <AiOutlineClose className="w-4 h-4 text-black cursor-pointer" style={{ strokeWidth: 2 }} />
                        </button>
                        <div className={`w-full flex flex-col mt-6 mx-6 justify-start ${centerTitle ? 'items-center text-center' : 'items-start'}`}>
                            {image}
                            <p className="mr-[10px] text-[#0d2c54] text-[20px] md:text-[24px] font-bold">{title}</p>
                            {subtitle && (
                                <p className="mr-[10px] text-[#656d79] text-[12px] md:text-[12px] mt-2 break-words whitespace-normal max-w-full">{subtitle}</p>
                            )}
                        </div>
                    </div>
                    <div
                        id="modal-content-container"
                        className="mx-5 h-full overflow-x-hidden overflow-y-hidden mb-6 justify-center items-center text-center py-[15px] pl-[30px] pr-[10px]"
                    >
                        {content}
                    </div>
                    <button
                        type="button"
                        onClick={() => closeModal()}
                        className="w-full bg-[#F7FAEF] flex justify-center items-center border-0 border-t border-blue-200 rounded-b-2xl py-8 text-[12px] cursor-pointer group"
                    >
                        <span className="text-[#5AB48A] relative">
                            Close
                            <span
                                id="animated-underline-close"
                                className="absolute left-0 bottom-0 h-[1px] w-0 bg-[#5AB48A] transition-all duration-500 ease-in-out group-hover:w-full"
                            />
                        </span>
                    </button>
                </div>
                </div>
            )}
		</>
	);
};

export default CMSModal;